import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Feather } from "lucide-react";
import { PageHeader } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { NewProductDialog } from "@/components/NewProductDialog";
import { api, fmtDate } from "@/lib/api";

const Stat = ({ label, value, id, i }) => (
  <div className={`bg-[#FFFDF9] border border-[#2A2325]/10 p-6 rise rise-${i}`}>
    <div className="eyebrow">{label}</div>
    <div className="font-display text-5xl mt-3" data-testid={id}>{value ?? "—"}</div>
  </div>
);

export default function Dashboard() {
  const [s, setS] = useState(null);
  useEffect(() => {
    api.get("/stats").then((r) => setS(r.data));
  }, []);

  return (
    <div>
      <PageHeader eyebrow="Good day, publisher" title="The Press Room">
        <NewProductDialog trigger={<Button className="rounded-none gap-2" data-testid="dashboard-new-product-btn"><Feather size={16} /> Begin a new title</Button>} />
      </PageHeader>
      <div className="px-6 md:px-12 py-10 space-y-12">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Stat i={1} label="Titles in library" value={s?.products} id="stat-products" />
          <Stat i={2} label="Ready to send" value={s?.ready} id="stat-ready" />
          <Stat i={3} label="Deliveries" value={s?.deliveries} id="stat-deliveries" />
          <Stat i={4} label="Customer downloads" value={s?.downloads} id="stat-downloads" />
        </div>
        <div className="grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-7 bg-[#FFFDF9] border border-[#2A2325]/10 rise rise-2">
            <div className="flex items-center justify-between p-6 border-b border-[#2A2325]/10">
              <h2 className="font-display text-2xl">Recent deliveries</h2>
              <Link to="/deliveries" className="text-sm text-[#5E2B38] flex items-center gap-1 hover:underline" data-testid="dashboard-all-deliveries-link">All <ArrowUpRight size={14} /></Link>
            </div>
            {s?.recent_deliveries?.length ? (
              <ul className="divide-y divide-[#2A2325]/10">
                {s.recent_deliveries.map((d) => (
                  <li key={d.id} className="px-6 py-4 flex justify-between gap-4 text-sm" data-testid={`recent-delivery-${d.id}`}>
                    <div className="min-w-0">
                      <div className="font-body-serif text-base truncate">{d.product_title}</div>
                      <div className="text-[#5C5255] truncate">{d.customer_email}</div>
                    </div>
                    <div className="text-right shrink-0 text-[#5C5255]">
                      <div>{fmtDate(d.created_at)}</div>
                      <div className="font-mono-meta text-xs">{d.download_count} dl · {d.email_status}</div>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="p-6 font-body-serif italic text-[#5C5255]">No deliveries yet. Open a finished title and choose “Send to customer”.</p>
            )}
          </div>
          <div className="lg:col-span-5 bg-[#38171E] text-[#F3E5AB] p-8 relative overflow-hidden rise rise-3">
            <div className="absolute inset-3 border border-[#D4AF37]/40 pointer-events-none" />
            <div className="text-[10px] tracking-[0.3em] text-[#D4AF37]">HOW THE PRESS WORKS</div>
            <ol className="mt-6 space-y-4 font-body-serif text-[15px] text-[#EDE3C8]">
              <li><span className="text-[#D4AF37] font-display italic text-xl mr-2">i.</span>Begin a title and choose its category.</li>
              <li><span className="text-[#D4AF37] font-display italic text-xl mr-2">ii.</span>Let AI write it from a topic, or convert your draft PDF into house style.</li>
              <li><span className="text-[#D4AF37] font-display italic text-xl mr-2">iii.</span>Polish section by section, then generate the PDF &amp; HTML — your logo sits on the back cover.</li>
              <li><span className="text-[#D4AF37] font-display italic text-xl mr-2">iv.</span>When it sells on softspinepress.com, send the buyer a private download link.</li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  );
}
